// ─── ThirdPartyDDQuestionnaireForm.jsx ────────────────────────────────────────
// Internal questionnaire form for Third Party DD assessment
// Features:
//  • Comprehensive multi-section questionnaire
//  • Auto-fill from external research
//  • Conditional questions based on answers
//  • Risk scoring calculation
//  • Save as draft or submit
// ────────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { db } from "./dbClient";
import { useAuditLog } from "./useAuditLog";
import ExternalResearch from "./ExternalResearch";
import { DD_QUESTIONNAIRE_SECTIONS, getQuestionsForSection } from "./DDQuestionnaireTemplate";
import { printToPDF } from "./exportUtils";

const RISK_WEIGHTS = {
  high_risk_jurisdictions: 15,
  pep_involvement: 20,
  regulatory_violations: 15,
  adverse_media: 10,
  edd_required: 25,
  politically_exposed: 20,
};

export default function ThirdPartyDDQuestionnaireForm({
  thirdPartyId,
  entityName,
  company,
  currentUser,
  onSave,
  onClose,
  initialData = null,
}) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);

  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState(initialData?.questionnaire_data || {});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showResearch, setShowResearch] = useState(false);
  const [researchFindings, setResearchFindings] = useState(
    initialData?.research_findings || null
  );

  // Calculate risk score based on answers
  const calculateRiskScore = () => {
    let score = 0;

    if (answers.high_risk_jurisdictions === "Yes - please specify") score += RISK_WEIGHTS.high_risk_jurisdictions;
    if (answers.politically_exposed === "Yes - please describe") score += RISK_WEIGHTS.politically_exposed;
    if (answers.regulatory_violations === "Yes - please describe") score += RISK_WEIGHTS.regulatory_violations;
    if (answers.adverse_media === "Yes - Adverse media found") score += RISK_WEIGHTS.adverse_media;
    if (answers.edd_required === "Yes - Strict EDD") score += RISK_WEIGHTS.edd_required;
    if (answers.pep_screening === "Yes - Screening performed" && answers.pep_screening_results) {
      score += RISK_WEIGHTS.pep_involvement;
    }

    return Math.min(score, 100);
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(p => ({ ...p, [questionId]: value }));
  };

  const handleAutoFillFromResearch = (findings) => {
    setResearchFindings(findings);
    const autoAnswers = {};

    // ── Section 1: Entity Identification & Ownership ────────────────────────
    if (findings.web?.length > 0) {
      autoAnswers.business_description = findings.web
        .slice(0, 3)
        .map(r => `${r.title}: ${r.snippet}`)
        .join("\n");
      autoAnswers.entity_description = findings.web[0]?.snippet || "";
    }

    // ── Section 2: Business Operations & Activities ────────────────────────
    if (findings.web?.length > 0) {
      // Extract services/operations from web results
      const servicesSnippet = findings.web
        .filter(r => r.snippet.toLowerCase().includes('service') || r.snippet.toLowerCase().includes('provide'))
        .map(r => r.snippet)
        .slice(0, 2)
        .join("; ");
      if (servicesSnippet) {
        autoAnswers.services_offered = servicesSnippet;
      }
    }

    // ── Section 3: Regulatory Status & Compliance ────────────────────────
    if (findings.regulatory?.length > 0) {
      autoAnswers.regulatory_oversight = "Yes - Regulated";
      autoAnswers.regulatory_details = findings.regulatory
        .map(r => `${r.regulator}: ${r.status}`)
        .join("\n");
    } else {
      autoAnswers.regulatory_oversight = "No - Not regulated";
    }

    // ── Section 4: Beneficial Owners & PEP Status ────────────────────────
    if (findings.pep_screening?.length > 0) {
      autoAnswers.pep_screening = "Yes - Screening performed";
      autoAnswers.pep_screening_results = findings.pep_screening
        .map(p => `${p.name}: ${p.type}`)
        .join("\n");
      autoAnswers.politically_exposed = "Yes - please describe";
    } else {
      autoAnswers.pep_screening = "Yes - No PEPs found";
      autoAnswers.politically_exposed = "No";
    }

    // ── Section 5: Financial Integrity & Banking ────────────────────────
    if (findings.financial?.length > 0) {
      autoAnswers.banking_relationships = findings.financial
        .filter(r => r.type === 'bank')
        .map(r => r.name)
        .join(", ");
    }

    // ── Section 6: Incident & Regulatory History ────────────────────────
    if (findings.news?.length > 0) {
      autoAnswers.adverse_media = "Yes - Adverse media found";
      autoAnswers.adverse_media_details = findings.news
        .slice(0, 5)
        .map(r => `${r.title}: ${r.snippet}`)
        .join("\n");
      autoAnswers.negative_news = "Yes";
    } else {
      autoAnswers.adverse_media = "Yes - Clean";
      autoAnswers.negative_news = "No";
    }

    if (findings.litigation?.length > 0) {
      autoAnswers.litigation_history = "Yes - Yes, litigation found";
      autoAnswers.litigation_details = findings.litigation
        .map(l => `${l.case}: ${l.status}`)
        .join("\n");
    } else {
      autoAnswers.litigation_history = "No";
    }

    // ── Section 7: Enhanced Due Diligence (EDD) Triggers ────────────────────────
    let eddScore = 0;
    if (findings.pep_screening?.length > 0) eddScore += 25;
    if (findings.news?.length > 0) eddScore += 15;
    if (findings.regulatory?.length > 0) eddScore += 10;
    if (findings.litigation?.length > 0) eddScore += 10;

    if (eddScore >= 30) {
      autoAnswers.edd_required = "Yes - Strict EDD";
    } else if (eddScore > 0) {
      autoAnswers.edd_required = "Yes - Standard EDD";
    } else {
      autoAnswers.edd_required = "No";
    }

    setAnswers(p => ({ ...p, ...autoAnswers }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    const riskScore = calculateRiskScore();

    const payload = {
      questionnaire_data: answers,
      risk_score: riskScore,
      research_findings: researchFindings,
      status: "draft",
    };

    try {
      if (initialData?.id) {
        // Update existing
        await db
          .from("third_party_dd_questionnaires")
          .update(payload)
          .eq("id", initialData.id)
          .execute();
        await log({
          action: "dd_questionnaire_updated",
          module: "third_party_dd",
          entityId: String(thirdPartyId),
          entityName,
          meta: { status: "draft", risk_score: riskScore },
        });
      } else {
        // Create new
        await db
          .from("third_party_dd_questionnaires")
          .insert({
            ...payload,
            third_party_dd_id: thirdPartyId,
            company_id: company.id,
          })
          .execute();
        await log({
          action: "dd_questionnaire_created",
          module: "third_party_dd",
          entityId: String(thirdPartyId),
          entityName,
          meta: { status: "draft", risk_score: riskScore },
        });
      }
      onSave?.({ ...payload, id: initialData?.id });
    } catch (err) {
      console.error("Error saving questionnaire:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    const riskScore = calculateRiskScore();

    const payload = {
      questionnaire_data: answers,
      risk_score: riskScore,
      research_findings: researchFindings,
      status: "submitted",
    };

    try {
      if (initialData?.id) {
        await db
          .from("third_party_dd_questionnaires")
          .update(payload)
          .eq("id", initialData.id)
          .execute();
      } else {
        await db
          .from("third_party_dd_questionnaires")
          .insert({
            ...payload,
            third_party_dd_id: thirdPartyId,
            company_id: company.id,
          })
          .execute();
      }
      await log({
        action: "dd_questionnaire_submitted",
        module: "third_party_dd",
        entityId: String(thirdPartyId),
        entityName,
        meta: { status: "submitted", risk_score: riskScore },
      });
      onSave?.({ ...payload, id: initialData?.id });
    } catch (err) {
      console.error("Error submitting questionnaire:", err);
    } finally {
      setSaving(false);
    }
  };

  const formatAnswerValue = (value) => {
    if (!value && value !== false && value !== 0) return "Not answered";
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      return value.map(v => {
        if (typeof v === 'object') return JSON.stringify(v);
        return String(v);
      }).join(', ') || "Not answered";
    }
    if (typeof value === 'object') {
      return JSON.stringify(value) || "Not answered";
    }
    return String(value).trim() || "Not answered";
  };

  const handleExportPDF = () => {
    const riskScore = calculateRiskScore();

    // Build questionnaire content for PDF
    let htmlContent = `
      <h1>Due Diligence Questionnaire</h1>
      <p><strong>Entity:</strong> ${entityName}</p>
      <p><strong>Risk Score:</strong> ${riskScore}/100</p>
      <hr/>
    `;

    // Add all sections and answers
    DD_QUESTIONNAIRE_SECTIONS.forEach((sec) => {
      htmlContent += `<h2>${sec.label}</h2>`;
      const sectionQuestions = getQuestionsForSection(sec.id);

      sectionQuestions.forEach((q) => {
        const rawAnswer = answers[q.id];
        const answer = formatAnswerValue(rawAnswer);
        const questionText = q.question || q.label || "Question";
        htmlContent += `
          <div style="margin: 12px 0; padding: 8px; border-left: 3px solid #ccc;">
            <p><strong>${questionText}</strong></p>
            <p style="margin: 0; white-space: pre-wrap;">${answer}</p>
          </div>
        `;
      });
    });

    // Add risk findings if available
    if (researchFindings) {
      htmlContent += `
        <h2>Research Findings</h2>
        <pre>${JSON.stringify(researchFindings, null, 2)}</pre>
      `;
    }

    printToPDF(`DD Questionnaire — ${entityName}`, htmlContent, company?.name);
  };

  const section = DD_QUESTIONNAIRE_SECTIONS[currentSection];
  const questions = getQuestionsForSection(section.id);
  const riskScore = calculateRiskScore();

  const S = {
    card: {
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: 20,
    },
    input: {
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      color: C.text,
      padding: "8px 12px",
      fontSize: 13,
      fontFamily: "'DM Mono', monospace",
      width: "100%",
      boxSizing: "border-box",
    },
    label: {
      color: C.textMid,
      fontSize: 11,
      display: "block",
      marginBottom: 6,
      fontWeight: 600,
      letterSpacing: "0.05em",
    },
    btn: (primary = false) => ({
      background: primary ? C.accent : "transparent",
      border: `1px solid ${primary ? C.accent : C.border}`,
      borderRadius: 6,
      color: primary ? C.bg : C.text,
      padding: "8px 16px",
      cursor: "pointer",
      fontSize: 12,
      fontFamily: "'DM Mono', monospace",
      fontWeight: 600,
    }),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ color: C.text, fontSize: 20, margin: 0, marginBottom: 4 }}>
            {entityName}
          </h2>
          <p style={{ color: C.textMid, fontSize: 12, margin: 0 }}>
            Due Diligence Questionnaire
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: C.accent, fontSize: 28, fontWeight: 700, fontFamily: "'Syne', sans-serif" }}>
            {riskScore}
          </div>
          <div style={{ color: C.textMid, fontSize: 11 }}>{t('thirdPartyDD.questionnaire.riskScore')}</div>
          <button
            onClick={() => setShowResearch(!showResearch)}
            style={{ ...S.btn(false), marginTop: 8, fontSize: 10, padding: "4px 8px" }}
          >
            {showResearch ? t('thirdPartyDD.questionnaire.hideResearch') : t('thirdPartyDD.questionnaire.runResearch')}
          </button>
        </div>
      </div>

      {showResearch && (
        <div style={S.card}>
          <ExternalResearch
            subjectName={entityName}
            subjectType="Business"
            onFindingsReady={handleAutoFillFromResearch}
          />
        </div>
      )}

      {/* Section Navigation */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
        {DD_QUESTIONNAIRE_SECTIONS.map((sec, idx) => (
          <button
            key={sec.id}
            onClick={() => setCurrentSection(idx)}
            style={{
              ...S.btn(idx === currentSection),
              textAlign: "center",
              fontSize: 11,
              padding: "10px 8px",
            }}
          >
            <div style={{ fontSize: 16, marginBottom: 2 }}>{sec.icon}</div>
            {sec.title.split(" ")[0]}
          </button>
        ))}
      </div>

      {/* Current Section */}
      <div style={S.card}>
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: C.text, fontSize: 16, margin: 0, marginBottom: 4 }}>
            {section.icon} {section.title}
          </h3>
          <p style={{ color: C.textMid, fontSize: 12, margin: 0 }}>
            {section.description}
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {questions.map(q => {
            // Check conditional visibility
            if (q.conditional) {
              const conditionalAnswers = Array.isArray(q.conditionalValue)
                ? q.conditionalValue
                : [q.conditionalValue];
              if (!conditionalAnswers.includes(answers[q.conditional])) {
                return null;
              }
            }

            return (
              <div key={q.id}>
                <label style={S.label}>
                  {q.question}
                  {q.required && <span style={{ color: C.danger }}>*</span>}
                </label>
                {q.hint && (
                  <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>
                    💡 {q.hint}
                  </div>
                )}

                {q.type === "text" && (
                  <input
                    type="text"
                    value={answers[q.id] || ""}
                    onChange={e => handleAnswerChange(q.id, e.target.value)}
                    placeholder={`Enter ${q.question.toLowerCase()}`}
                    style={S.input}
                  />
                )}

                {q.type === "email" && (
                  <input
                    type="email"
                    value={answers[q.id] || ""}
                    onChange={e => handleAnswerChange(q.id, e.target.value)}
                    placeholder="email@example.com"
                    style={S.input}
                  />
                )}

                {q.type === "date" && (
                  <input
                    type="date"
                    value={answers[q.id] || ""}
                    onChange={e => handleAnswerChange(q.id, e.target.value)}
                    style={S.input}
                  />
                )}

                {q.type === "textarea" && (
                  <textarea
                    value={answers[q.id] || ""}
                    onChange={e => handleAnswerChange(q.id, e.target.value)}
                    rows={3}
                    style={{ ...S.input, resize: "vertical", fontFamily: "'DM Mono', monospace" }}
                  />
                )}

                {q.type === "select" && (
                  <select
                    value={answers[q.id] || ""}
                    onChange={e => handleAnswerChange(q.id, e.target.value)}
                    style={S.input}
                  >
                    <option value="">Select…</option>
                    {q.options.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                )}

                {q.type === "checkbox" && (
                  <button
                    onClick={() => handleAnswerChange(q.id, !answers[q.id])}
                    style={{
                      ...S.btn(answers[q.id]),
                      textAlign: "left",
                      justifyContent: "flex-start",
                    }}
                  >
                    <span style={{ marginRight: 8 }}>
                      {answers[q.id] ? "☑" : "☐"}
                    </span>
                    {q.label || q.question}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Regulatory frameworks */}
        <div
          style={{
            marginTop: 20,
            padding: "10px 12px",
            background: `${C.accent}15`,
            borderRadius: 6,
            fontSize: 11,
            color: C.textMid,
          }}
        >
          <strong style={{ color: C.accent }}>Regulatory Coverage:</strong> This section covers{" "}
          {Array.from(
            new Set(
              questions
                .flatMap(q => q.regulatory || [])
            )
          ).join(", ")}
        </div>
      </div>

      {/* Navigation & Actions */}
      <div style={{ display: "flex", gap: 12, justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            style={{ ...S.btn(false), opacity: currentSection === 0 ? 0.5 : 1 }}
          >
            ← {t('common.previous')}
          </button>
          <button
            onClick={() =>
              setCurrentSection(
                Math.min(DD_QUESTIONNAIRE_SECTIONS.length - 1, currentSection + 1)
              )
            }
            disabled={currentSection === DD_QUESTIONNAIRE_SECTIONS.length - 1}
            style={{
              ...S.btn(false),
              opacity:
                currentSection === DD_QUESTIONNAIRE_SECTIONS.length - 1 ? 0.5 : 1,
            }}
          >
            {t('common.next')} →
          </button>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            style={S.btn(false)}
          >
            {saving ? t('thirdPartyDD.questionnaire.savingDraft') : t('thirdPartyDD.questionnaire.saveDraft')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={S.btn(true)}
          >
            {saving ? t('thirdPartyDD.questionnaire.submitting') : t('thirdPartyDD.questionnaire.submitQuestionnaire')}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={saving}
            style={S.btn(false)}
          >
            📄 Export PDF
          </button>
          <button onClick={onClose} style={S.btn(false)}>
            Close
          </button>
        </div>
      </div>

      {/* Progress */}
      <div style={{ textAlign: "center", color: C.textMid, fontSize: 11 }}>
        Section {currentSection + 1} of {DD_QUESTIONNAIRE_SECTIONS.length}
      </div>
    </div>
  );
}
